import { useFormik } from 'formik';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useGlobalToastHandler } from 'views/mutual/globalToast/hooks';
import {
  GetCompatibleDocument,
  GetCompatibleQuery,
  GetCompatibleQueryVariables,
  useApplySymbolMutation,
  useConfirmApplySymbolMutation,
  OverseasRealEstateSymbolDetailInfo,
  GetPreApplicationStatusQuery,
  GetPreApplicationStatusQueryVariables,
  GetPreApplicationStatusDocument,
  useGetOverseasRealEstateSymbolInfoQuery,
  useGetDateTimeQuery,
  useGetAccountInfoQuery,
  GetAccountInfoQueryVariables,
  GetAccountInfoQuery,
  GetAccountInfoDocument,
} from 'views/utils/graphql/graphql';
import useOnceSubmit from 'views/utils/useOnceSubmit';
import * as Yup from 'yup';
import { calcInterest } from '../calcInterest';
import { DateFormatType, formatDate } from 'views/utils/converter';
import { Client, Context as UrqlContext } from 'urql';
import { ApplyAssetType, ApplySymbolRequestType } from 'views/utils/enums';
import { formErrorScroll } from 'views/mutual/formErrorScroll';
import { SelectorOptionType } from 'views/templates/module/selector';
import { UnitSelectorPosition } from 'views/templates/module/unitSelector';

export enum SymbolPageStep {
  Detail,
  CompatibilityCheck,
  ApplicationStatus,
  Confirm,
  Complete,
  ContractComplete,
}

export type ApplySymbolInput = {
  symbol: string;
  amount: number | null;
  clientRequestId: string | null;
  transferDate: Date | null;
  isContractDocumentOpened: boolean; // 契約締結前交付書面を開いたかどうか
  isContractDocumentCheck: boolean;
  isCheck2: boolean;
  isCheck3: boolean;
  isCheck4: boolean;
  isCheck5: boolean;
  isCheck6: boolean;
  isCheck7: boolean;
  isCheck8: boolean;
  isCheck9: boolean;
  isCheck10: boolean;
  applicationStartDateTime: string;
};

const transferDateRangeError = (transferStartDate: string, transferEndDate: string) => {
  return `${transferStartDate}～${transferEndDate}の範囲にしてください`;
};

const transferDateSchema = (
  transferStartDate: string | null | undefined,
  transferEndDate: string | null | undefined,
) => {
  // 入力制御しているが念のため
  const base = Yup.date().typeError('日付で入力してください').required('必須です');
  if (transferStartDate && transferEndDate) {
    return base
      .min(new Date(transferStartDate), transferDateRangeError(transferStartDate, transferEndDate))
      .max(new Date(transferEndDate), transferDateRangeError(transferStartDate, transferEndDate));
  }

  return base;
};
const limitDateCheck = (today: Date, limitDate: Date | null): boolean => {
  if (!limitDate) return false;

  const addDays = 1;
  limitDate.setDate(limitDate.getDate() + addDays);

  return today < limitDate;
};

const applySymbolInputValidation = (
  transferStartDate: string | null | undefined,
  transferEndDate: string | null | undefined,
) =>
  Yup.object().shape({
    symbol: Yup.string().required(),
    amount: Yup.number()
      .typeError('半角数字で入力してください')
      .required('必須です')
      .min(1, '1以上で入力してください')
      .positive('1以上で入力してください')
      .integer('整数で入力してください'),
    transferDate: transferDateSchema(transferStartDate, transferEndDate),
    isContractDocumentOpened: Yup.boolean().isTrue(),
    isContractDocumentCheck: Yup.boolean().isTrue(),
    isCheck2: Yup.boolean().isTrue(),
    isCheck3: Yup.boolean().isTrue(),
    isCheck4: Yup.boolean().isTrue(),
    isCheck5: Yup.boolean().isTrue(),
    isCheck6: Yup.boolean().isTrue(),
    isCheck7: Yup.boolean().isTrue(),
    isCheck8: Yup.boolean().isTrue(),
    isCheck9: Yup.boolean().isTrue(),
    isCheck10: Yup.boolean().isTrue(),
  });

// falseの場合は条件を満たしていないので申し込めない状態
const canApplySymbol = (
  symbol: OverseasRealEstateSymbolDetailInfo | null | undefined,
  input: ApplySymbolInput,
): boolean => {
  if (
    symbol &&
    // apiのレスポンスとして申し込めるかのフラグがある
    symbol.Tradable &&
    input.isContractDocumentCheck &&
    input.isCheck2 &&
    input.isCheck3 &&
    input.isCheck4 &&
    input.isCheck5 &&
    input.isCheck6 &&
    input.isCheck7 &&
    input.isCheck8 &&
    input.isCheck9 &&
    input.isCheck10
  ) {
    return true;
  } else {
    return false;
  }
};

type SymbolPageProps = {
  symbol: string;
};

export const useOverseasRealEstateSymbolPage = ({ symbol }: SymbolPageProps) => {
  const [symbolPageStep, setSymbolPageStep] = useState<SymbolPageStep>(SymbolPageStep.Detail);
  const [symbolDetailInfo, _reload] = useGetOverseasRealEstateSymbolInfoQuery({
    variables: {
      symbol,
    },
    requestPolicy: 'network-only',
  });
  const [dateTime, getDataTime] = useGetDateTimeQuery({
    requestPolicy: 'network-only',
  });
  const [applySymbolRes, applySymbol] = useApplySymbolMutation();
  const [confirmApplySymbolRes, confirmApplySymbol] = useConfirmApplySymbolMutation();
  const { showError, messageClose } = useGlobalToastHandler();
  const applyOnlyCodeList = [409, 411, 416, 417, 432];
  const [selectedAmount, setSelectedAmount] = useState<SelectorOptionType | null>(null); //申込個数選択
  const [inputAmount, setInputAmount] = useState<number | null>(null); //手入力個数
  const [showManualQuantity, setShowManualQuantity] = useState<boolean>(false); //手入力フラグ
  const [accountInfo, _] = useGetAccountInfoQuery({
    requestPolicy: 'network-only',
  });

  const client = useContext(UrqlContext) as Client;

  const onSubmit = useOnceSubmit<ApplySymbolInput>(
    {
      onPreviousSubmit: async (input) => {
        return canApplySymbol(symbolDetailInfo.data?.GetOverseasRealEstateSymbolInfo, input);
      },
      onSubmit: async (input) => {
        switch (symbolPageStep) {
          case SymbolPageStep.Detail: {
            // ConfirmでAPIのエラー → Detailに戻って修正 → Confirmに遷移した場合はエラーが消えるようにする
            messageClose();
            getDataTime();
            // 適合性チェックが済んでいない場合は適合性チェックを表示する
            const compatibleRes = await client.query<GetCompatibleQuery, GetCompatibleQueryVariables>(
              GetCompatibleDocument,
              {},
              {
                requestPolicy: 'network-only',
              },
            );

            if (!compatibleRes.data?.GetAccountInfo?.Compatible?.OverseasRealEstate) {
              setSymbolPageStep(SymbolPageStep.CompatibilityCheck);
              return;
            }

            //fatca回答チェック
            const accountRes = await client.query<GetAccountInfoQuery, GetAccountInfoQueryVariables>(
              GetAccountInfoDocument,
              {},
              {
                requestPolicy: 'network-only',
              },
            );
            const showFatcaPage = !(
              accountRes.data?.GetAccountInfo?.IsInvested ||
              (accountRes.data?.GetAccountInfo?.KFW8BenE && accountRes.data?.GetAccountInfo?.KFFatca)
            );

            if (showFatcaPage) {
              setSymbolPageStep(SymbolPageStep.ApplicationStatus);
              return;
            }

            const res = await confirmApplySymbol({
              input: {
                Symbol: input.symbol!,
                Amount: input.amount!,
                TransferDate: formatDate(input.transferDate, DateFormatType.Slash_yyyyMMdd),
                IntroductionConfirmation: 3, //この機能は現在廃止しているため3で固定
                RequestType: ApplySymbolRequestType.Confirm,
                AssetType: ApplyAssetType.OverseasRealEstate,
                ApplicationStartDate: input.applicationStartDateTime,
              },
            });

            if (!res?.data) return;
            if (res.data.ApplySymbol?.Code === 0 || applyOnlyCodeList.includes(res.data.ApplySymbol?.Code ?? -1)) {
              //undefinedの時はfalseになるように-1 を入れる
              setSymbolPageStep(SymbolPageStep.Confirm);
            } else {
              setSymbolPageStep(SymbolPageStep.Detail);
              showError(res.data.ApplySymbol?.Message);
            }
            break;
          }
          case SymbolPageStep.CompatibilityCheck: {
            // ConfirmでAPIのエラー → Detailに戻って修正 → Confirmに遷移した場合はエラーが消えるようにする
            messageClose();

            //fatca回答チェック
            const accountRes = await client.query<GetAccountInfoQuery, GetAccountInfoQueryVariables>(
              GetAccountInfoDocument,
              {},
              {
                requestPolicy: 'network-only',
              },
            );
            const showFatcaPage = !(
              accountRes.data?.GetAccountInfo?.IsInvested ||
              (accountRes.data?.GetAccountInfo?.KFW8BenE && accountRes.data?.GetAccountInfo?.KFFatca)
            );

            if (showFatcaPage) {
              setSymbolPageStep(SymbolPageStep.ApplicationStatus);
              return;
            }
            const res = await confirmApplySymbol({
              input: {
                Symbol: input.symbol!,
                Amount: input.amount!,
                TransferDate: formatDate(input.transferDate, DateFormatType.Slash_yyyyMMdd),
                IntroductionConfirmation: 3, //この機能は現在廃止しているため3で固定
                RequestType: ApplySymbolRequestType.Confirm,
                AssetType: ApplyAssetType.OverseasRealEstate,
                ApplicationStartDate: input.applicationStartDateTime,
              },
            });

            if (!res?.data) return;
            if (res.data.ApplySymbol?.Code === 0 || applyOnlyCodeList.includes(res.data.ApplySymbol?.Code ?? -1)) {
              //undefinedの時はfalseになるように-1 を入れる
              setSymbolPageStep(SymbolPageStep.Confirm);
            } else {
              setSymbolPageStep(SymbolPageStep.Detail);
              showError(res.data.ApplySymbol?.Message);
            }
            break;
          }
          case SymbolPageStep.ApplicationStatus: {
            // ConfirmでAPIのエラー → Detailに戻って修正 → Confirmに遷移した場合はエラーが消えるようにする
            messageClose();

            const res = await confirmApplySymbol({
              input: {
                Symbol: input.symbol!,
                Amount: input.amount!,
                TransferDate: formatDate(input.transferDate, DateFormatType.Slash_yyyyMMdd),
                IntroductionConfirmation: 3, //この機能は現在廃止しているため3で固定
                RequestType: ApplySymbolRequestType.Confirm,
                AssetType: ApplyAssetType.OverseasRealEstate,
                ApplicationStartDate: input.applicationStartDateTime,
              },
            });

            if (!res?.data) return;
            if (res.data.ApplySymbol?.Code === 0 || applyOnlyCodeList.includes(res.data.ApplySymbol?.Code ?? -1)) {
              //undefinedの時はfalseになるように-1 を入れる
              setSymbolPageStep(SymbolPageStep.Confirm);
            } else {
              setSymbolPageStep(SymbolPageStep.Detail);
              showError(res.data.ApplySymbol?.Message);
            }
            break;
          }
          case SymbolPageStep.Confirm: {
            const appSymbolRes = await applySymbol({
              input: {
                Symbol: input.symbol!,
                Amount: input.amount!,
                TransferDate: formatDate(input.transferDate, DateFormatType.Slash_yyyyMMdd),
                IntroductionConfirmation: 3, //この機能は現在廃止しているため3で固定
                RequestType: ApplySymbolRequestType.Apply,
                AssetType: ApplyAssetType.OverseasRealEstate,
                ApplicationStartDate: input.applicationStartDateTime,
                ClientRequestId: input.clientRequestId!,
              },
            });
            if (!appSymbolRes?.data) return;
            if (appSymbolRes.data.ApplySymbol?.Code === 0) {
              setSymbolPageStep(SymbolPageStep.ContractComplete);
            } else if (applyOnlyCodeList.includes(appSymbolRes.data.ApplySymbol?.Code ?? -1)) {
              //undefinedの時はfalseになるように-1 を入れる
              setSymbolPageStep(SymbolPageStep.Complete);
            } else {
              showError(appSymbolRes.data.ApplySymbol?.Message);
            }
            break;
          }
          case SymbolPageStep.Complete:
          case SymbolPageStep.ContractComplete:
            break;
        }
      },
    },
    [symbolPageStep, setSymbolPageStep, applySymbol, showError, messageClose],
  );

  const backDetailPage = useCallback(() => {
    switch (symbolPageStep) {
      case SymbolPageStep.Detail: {
        break;
      }
      case SymbolPageStep.Confirm: {
        setSymbolPageStep(SymbolPageStep.Detail);
        break;
      }
      case SymbolPageStep.Complete:
        break;
    }
  }, [symbolPageStep, setSymbolPageStep]);

  const formik = useFormik<ApplySymbolInput>({
    initialValues: {
      symbol,
      amount: null,
      clientRequestId: null,
      transferDate: null,
      isContractDocumentOpened: false,
      isContractDocumentCheck: false,
      isCheck2: false,
      isCheck3: false,
      isCheck4: false,
      isCheck5: false,
      isCheck6: false,
      isCheck7: false,
      isCheck8: false,
      isCheck9: false,
      isCheck10: false,
      applicationStartDateTime: '',
    },
    onSubmit,
    validationSchema: applySymbolInputValidation(
      symbolDetailInfo.data?.GetOverseasRealEstateSymbolInfo?.TransferStartDate,
      symbolDetailInfo.data?.GetOverseasRealEstateSymbolInfo?.TransferEndDate,
    ),
    validateOnBlur: true,
    validateOnChange: true,
  });

  useEffect(() => {
    if (!dateTime.data?.GetDateTime) return;
    formik.values.applicationStartDateTime = dateTime.data?.GetDateTime?.Current!;
  }, [dateTime]);

  // formik.values.amountはx百万円 amountRawはx円
  const amounts = useMemo<{ interest: number | null; amountRaw: number | null; totalAmountRaw: number | null }>(() => {
    if (!formik.values.amount) {
      return {
        interest: null,
        amountRaw: null,
        totalAmountRaw: null,
      };
    }

    const amountRaw = formik.values.amount;

    if (formik.values.transferDate && symbolDetailInfo.data?.GetOverseasRealEstateSymbolInfo) {
      const symbol = symbolDetailInfo.data?.GetOverseasRealEstateSymbolInfo;
      const interest = calcInterest(
        amountRaw,
        formik.values.transferDate!,
        new Date(symbol.BusinessStartDate),
        symbol.TransferInterestPattern,
        symbol.TransferInterest,
        symbol.BoundaryAmount!,
        symbol.LowerRate!,
        symbol.UpperRate!,
      );

      return {
        interest: interest,
        amountRaw,
        totalAmountRaw: amountRaw + interest,
      };
    } else {
      return {
        interest: null,
        amountRaw: null,
        totalAmountRaw: null,
      };
    }
  }, [symbolDetailInfo.data?.GetOverseasRealEstateSymbolInfo, formik.values.amount, formik.values.transferDate]);
  const transferDateProps = {
    date: formik.values.transferDate,
    name: 'transferDate',
    // 過去日かどうかを考慮してTransferStartDateが返ってくるので過去日かどうかをクライアントは考慮しない
    minDate: new Date('1700/1/1'),
    maxDate: new Date(),
    onChange: (value: Date) => formik.setFieldValue('transferDate', value),
    onBlur: formik.handleBlur,
    isWeekEndDisable: true,
    error: formik.touched.transferDate ? formik.errors.transferDate : '',
  };

  const checkContractDocumentProps = {
    checked: formik.values.isContractDocumentCheck,
    onChange: () => formik.setFieldValue('isContractDocumentCheck', !formik.values.isContractDocumentCheck),
    linkClicked: formik.values.isContractDocumentOpened,
    onClick: () => formik.setFieldValue('isContractDocumentOpened', true),
    linkClickedRequired: true,
  };

  // formik.values.amountはx百万円 amountRawはx円
  // const amounts = useMemo<{ interest: number | null; amountRaw: number | null; totalAmountRaw: number | null }>(() => {
  //   if (!formik.values.amount) {
  //     return {
  //       interest: null,
  //       amountRaw: null,
  //       totalAmountRaw: null,
  //     };
  //   }

  //   const amountRaw = formik.values.amount;

  //   if (formik.values.transferDate && symbolDetailInfo.data?.GetSymbolInfo) {
  //     const symbol = symbolDetailInfo.data?.GetSymbolInfo;
  //     const interest = calcInterest(
  //       amountRaw,
  //       formik.values.transferDate!,
  //       new Date(symbol.LeaseStartDate),
  //       symbol.TransferInterestPattern,
  //       symbol.TransferInterest,
  //     );

  //     return {
  //       interest: interest,
  //       amountRaw,
  //       totalAmountRaw: amountRaw + interest,
  //     };
  //   } else {
  //     return {
  //       interest: null,
  //       amountRaw: null,
  //       totalAmountRaw: null,
  //     };
  //   }
  // }, [symbolDetailInfo.data?.GetSymbolInfo, formik.values.amount, formik.values.transferDate]);

  const limitDateResult = limitDateCheck(
    new Date(formik.values.applicationStartDateTime),
    symbolDetailInfo.data?.GetOverseasRealEstateSymbolInfo?.ApplicationLimitDate
      ? new Date(symbolDetailInfo.data?.GetOverseasRealEstateSymbolInfo.ApplicationLimitDate)
      : null,
  );

  const canApplyResult =
    canApplySymbol(symbolDetailInfo.data?.GetOverseasRealEstateSymbolInfo, formik.values) && limitDateResult;

  //選択リスト最大値
  const limit = Number(
    symbolDetailInfo.data?.GetOverseasRealEstateSymbolInfo?.AmountOptions?.[
      symbolDetailInfo.data?.GetOverseasRealEstateSymbolInfo?.AmountOptions?.length - 1
    ]?.replaceAll(',', ''),
  );
  //販売単位
  const step = symbolDetailInfo.data?.GetOverseasRealEstateSymbolInfo?.SalesUnitCtrl!;
  //手入力最小値
  const manualInputMin = limit + step;
  //手入力最大値
  const manualInputMax = symbolDetailInfo.data?.GetOverseasRealEstateSymbolInfo?.MaxInvestmentAmount;
  //選択リスト 選択リストの最大値よりも個数の最大値が大きい場合手入力
  const amountOptions = symbolDetailInfo.data?.GetOverseasRealEstateSymbolInfo?.AmountOptions
    ? [
        ...symbolDetailInfo.data.GetOverseasRealEstateSymbolInfo.AmountOptions.map((option) => {
          return { label: `${option}円`, value: option } as SelectorOptionType;
        }),
        ...(manualInputMax && limit < manualInputMax
          ? [{ label: `${manualInputMin.toLocaleString()}円以上は手入力をお願いします`, value: manualInputMin }]
          : []),
      ]
    : [];

  const amountProps = {
    unitSelectorProps: {
      options: amountOptions,
      unit: UnitSelectorPosition.after,
      selectedOption: selectedAmount?.value?.toString() ?? '', // 数値を文字列として渡す
      setSelectedOption: (value: string) => {
        const selected = amountOptions?.find((option) => option.value.toString() === value);
        setSelectedAmount(selected ?? null);
        const valueNum = Number(value?.replaceAll(',', ''));
        const isShowManualQuantity = limit < valueNum;
        setShowManualQuantity(isShowManualQuantity);
        setInputAmount(isShowManualQuantity ? manualInputMin : null);
        formik.setFieldValue('amount', valueNum);
      },
      disabled: !symbolDetailInfo.data?.GetOverseasRealEstateSymbolInfo,
      error: formik.touched.amount ? formik.errors.amount : '',
    },
    spinboxProps: {
      name: 'symbolSpinbox',
      value: inputAmount,
      onChange: (value: number) => {
        if (manualInputMax && manualInputMax < value) {
          setInputAmount(manualInputMax);
          return;
        }
        setInputAmount(value);
      },
      onBlur: async (e: any) => {
        //値が無い場合と最小値を下回った場合は最小値にする
        let newAmount = manualInputMin;

        //最大値を超えた場合は最大値にする
        if (inputAmount && manualInputMax && manualInputMax < inputAmount) {
          newAmount = manualInputMax;
        }
        //10刻みで切り捨て
        else if (inputAmount && manualInputMin && manualInputMin < inputAmount) {
          const truncatedValue = Math.floor(inputAmount / step) * step;
          newAmount = truncatedValue;
        }
        await formik.setFieldValue('amount', newAmount);
        setInputAmount(newAmount);
      },
      min: manualInputMin,
      max: manualInputMax,
      step: step,
      unit: '円',
    },
    showManualQuantity: showManualQuantity,
  };

  useEffect(() => {
    formErrorScroll();
  }, [formik.isSubmitting]);

  return {
    symbolPageStep,
    symbolDetailInfo,
    applySymbolRes,
    confirmApplySymbolRes,
    formik,
    canApplySymbol: canApplySymbol(symbolDetailInfo.data?.GetOverseasRealEstateSymbolInfo, formik.values),
    interest: amounts.interest,
    amountRaw: amounts.amountRaw,
    totalAmountRaw: amounts.totalAmountRaw,
    transferDateProps,
    checkContractDocumentProps,
    backDetailPage,
    amountProps,
  };
};
